
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { toast } from "react-toastify";

import "../../Styles/pages/suyool-to-suyool.scss";

import {
  faChevronRight,
  faFileArrowDown,
} from "@fortawesome/free-solid-svg-icons";
import {
  setActionPayload,
  setActionType,
} from "../../../features/authorizationSlice";
import { ACTION_TYPES } from "../../../app/actionTypes";
import {
  capitalizeFirstLetters,
  formatAmount,
  normalizeCurrencyCode,
} from "../../../features/ValueFormater";
import { exportToExcel } from "../../../utils/functions";
import { roleAuth } from "../../../utils/functionAuthorization";
import useNewStatementOfAccountAPI from "../../api/statementOfAccountAPI";
import useNewCommonAPI from "../../api/commonAPI";
import useNewTransactionDetailsAPI from "../../api/transactionDetailsAPI";
import useNewBslAccountAPI from "../../api/bslAccountAPI";
import DataTable from "../Table";
import TableFilters from "../Common/TableFilters";
import TableExportButton from "../Common/TableExportButton";
import TransactionDetailsPanel from "../TransactionDetailsPanel";

import StatementExportModal from "./ExportModal";
import { StatusBadge } from "../Table/cells";
import {
  createDefaultDateRange,
  fromPickerRange,
  toPickerRange,
} from "../../utils/dateRange";
import useLoadMoreList from "../../hooks/useLoadMoreList";
import useDetailsPanelAnchor from "../../hooks/useDetailsPanelAnchor";
import RepeatButton from "../RepeatButton/RepeatButton";

const ACCESS_POINTS = {
  detailsLBP: "VIEW_TRANSACTIONS_DETAILS_OF_LBP_ACCOUNT",
  detailsUSD: "VIEW_TRANSACTIONS_DETAILS_OF_USD_ACCOUNT",
  detailsEUR: "VIEW_TRANSACTIONS_DETAILS_OF_EUR_ACCOUNT",
};

const LEGAL_TRANSACTION_OPTIONS = [
  { value: "All", label: "All Transactions" },
  { value: "CardRequest", label: "Card Request" },
  { value: "CardTransactions", label: "Card Transactions" },
  { value: "CloseBalance", label: "Close Balance" },
  { value: "Exchange", label: "Exchange" },
  { value: "SuyoolToSuyool", label: "Suyool To Suyool" },
  { value: "Payroll", label: "Payroll" },
  { value: "CashDeposit", label: "Cash Deposit" },
  { value: "CashWithdrawal", label: "Cash Withdrawal" },
];

function getStatementRowIdentity(row) {
  if (!row) return null;

  return (
    row?.statementDetailID ||
    row?.StatementDetailID ||
    row?.Transfer ||
    row?.TransferID ||
    [
      row?.Transaction,
      row?.PaymentDate,
      row?.RowDate,
      row?.Status,
      row?.Account,
      row?.Type,
      row?.Description,
      row?.Amount,
      row?.Sign,
      row?.WalletID,
      row?.CurrencyId,
      row?.Row,
    ]
      .filter(
        (value) =>
          value !== null &&
          value !== undefined &&
          value !== ""
      )
      .join("|") ||
    null
  );
}

function normalizeStatementRow(row, fallbackCurrencyId) {
  const transactionId =
    row?.Transaction ||
    row?.Transfer ||
    row?.OrderID ||
    row?.id ||
    "";

  return {
    id:
      getStatementRowIdentity(row) ||
      transactionId ||
      row?.Row ||
      `${row?.PaymentDate || ""}-${row?.Description || ""}-${row?.Amount || ""}`,
    transaction: row?.Transaction || "-",
    paymentDate: row?.PaymentDate,
    description: row?.Description || "-",
    account: row?.Account || "-",
    type: row?.Type || "-",
    amount: Number(row?.Amount ?? 0),
    balance: Number(row?.Balance ?? 0),
    status: row?.Status || "",
    sign: row?.Sign || "",
    currencyId: row?.CurrencyId || row?.CurrencyID || fallbackCurrencyId,
    transfer: row?.Transfer || "",
    statementDetailID:
      row?.statementDetailID ||
      row?.StatementDetailID ||
      "",
    rowDate: row?.RowDate || "",
    isPayrollReference: Boolean(row?.IsPayrollReference),
    isRepeated: Boolean(row?.IsRepeated),
    original: row,
  };
}

function getTransactionReference(row) {
  return [
    row?.transfer,
    row?.transaction,
    row?.original?.TransactionID,
    row?.original?.TransactionId,
    row?.original?.TransferID,
    row?.original?.TransferId,
    row?.original?.OrderID,
  ].find((value) => {
    const normalized = String(value ?? "").trim();
    return normalized && normalized !== "-" && normalized !== "0";
  });
}

function getRowDetailsId(row, isBslAccount) {
  if (!isBslAccount) {
    return row?.transfer || row?.transaction || row?.id;
  }

  const transactionId = getTransactionReference(row);
  if (transactionId) return transactionId;
  return (
    row?.statementDetailID ||
    row?.original?.RowId ||
    row?.original?.RowID ||
    row?.original?.Row ||
    row?.id ||
    ""
  );
}

function hasZeroTransaction(row) {
  const transaction = row?.original?.Transaction ?? row?.transaction;
  const value = String(transaction ?? "").trim();
  return value !== "" && Number(value) === 0;
}

function getBslRowDetails(row) {
  if (!row) return null;

  return {
    Type: row.type !== "-" ? row.type : "BSL Transaction",
    TransactionID: getTransactionReference(row) || undefined,
    Date: row.paymentDate,
    Description: row.description !== "-" ? row.description : undefined,
    Amount: (row.sign === "-" ? -1 : 1) * Math.abs(Number(row.amount ?? 0)),
    CurrencyID: row.currencyId,
    Status: row.status,
  };
}

function normalizeExportRow(row, currencyId) {
  const paymentDate = row?.PaymentDate
    ? new Date(row.PaymentDate)
    : null;

  const formattedDate =
    paymentDate && !Number.isNaN(paymentDate.getTime())
      ? paymentDate.toLocaleDateString()
      : row?.PaymentDate || "";

  const formattedTime =
    paymentDate && !Number.isNaN(paymentDate.getTime())
      ? paymentDate.toLocaleTimeString()
      : "";

  return {
    Transaction: row?.Transaction,
    Reference: row?.Reference,
    PaymentDate: formattedDate,
    PaymentTime: formattedTime,
    Details: row?.Description,
    Currency: normalizeCurrencyCode(row?.CurrencyId || row?.CurrencyID || currencyId),
    Amount:
      row?.Sign === "-"
        ? -Number(row?.Amount ?? 0)
        : Number(row?.Amount ?? 0),
    Balance: row?.Balance,
    Commission:
      Number(row?.Commission ?? 0) === 0
        ? 0
        : row?.Sign === "-"
          ? -Number(row?.Commission ?? 0)
          : Number(row?.Commission ?? 0),
    Rebate:
      Number(row?.Rebate ?? 0) === 0
        ? 0
        : row?.Sign === "-"
          ? -Number(row?.Rebate ?? 0)
          : Number(row?.Commission ?? 0),
    Status: /^\d/.test(String(row?.Status || ""))
      ? "Successful"
      : capitalizeFirstLetters(
        String(row?.Status || "")
      ),
  };
}

async function downloadRemotePdf(url, filename) {
  const triggerDirectDownload = () => {
    const link = document.createElement("a");
    link.href = url;
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  try {
    const response = await fetch(url, {
      method: "GET",
    });

    if (!response.ok) {
      throw new Error("Failed to fetch statement");
    }

    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = blobUrl;
    link.download = filename;

    document.body.appendChild(link);
    link.click();
    link.remove();

    window.URL.revokeObjectURL(blobUrl);
  } catch (error) {
    triggerDirectDownload();
  }
}

export default function LegalStatementHistory({
  currencyId,
  selectedAccount,
  onBalancesChange,
  onOpenQrAfterGenerate,
  accountSelected,
  accessPoints,

}) {
  const dispatch = useDispatch();

  const {
    GetSOAList,
    GetSOAExport,
    GetSuyoolAccountBalance,
    exportLoading,
  } = useNewStatementOfAccountAPI();

  const { getAllCurrencies } = useNewCommonAPI();

  const {
    GetTransactionDetails,
    resetTransactionDetails,
    transactionDetails,
    detailsLoading,
    detailsError,
  } = useNewTransactionDetailsAPI();

  const {
    AccessToken,
    DownloadTransferStatment,
  } = useNewBslAccountAPI();


  const [dateRange, setDateRange] = useState(() =>
    createDefaultDateRange()
  );
  const [transactionType, setTransactionType] =
    useState("All");
  const [searchDraft, setSearchDraft] = useState("");
  const [listError, setListError] = useState("");
  const [activeRowIndex, setActiveRowIndex] =
    useState(-1);
  const [activeRow, setActiveRow] = useState(null);
  const [exportMenuOpen, setExportMenuOpen] =
    useState(false);
  const [exportModalOpen, setExportModalOpen] =
    useState(false);
  const [downloadingStatementId, setDownloadingStatementId] = useState(null);
  const hasSearchButton = true;
  const isBslAccount = Number(accountSelected) === 2;
  const {
    anchorStyle,
    anchorToRow,
    containerRef,
    resetAnchor,
  } = useDetailsPanelAnchor();

  const canViewDetails =
    (currencyId === 1 &&
      roleAuth(
        "statementofaccount",
        ACCESS_POINTS.detailsUSD
      )) ||
    (currencyId === 2 &&
      roleAuth(
        "statementofaccount",
        ACCESS_POINTS.detailsLBP
      )) ||
    (currencyId === 178 &&
      roleAuth("statementofaccount", ACCESS_POINTS.detailsEUR));

  const currencies = useMemo(() => {
    try {
      const rawValue =
        sessionStorage.getItem("Currencies");

      return rawValue
        ? JSON.parse(rawValue)
        : [];
    } catch {
      return [];
    }
  }, []);

  useEffect(() => {
    if (!currencies.length) {
      getAllCurrencies().catch(() => { });
    }
  }, [currencies.length]);


  const loadStatementRows = useCallback(
    ({ lastDate }) =>
      GetSOAList({
        CurrencyID: currencyId,
        lastDate,
        DateFrom: dateRange.from,
        DateTo: dateRange.to,
        TransactionType: transactionType,
        AccountType: accountSelected,
        Input: searchDraft,
      }),
    [
      GetSOAList,
      accountSelected,
      currencyId,
      dateRange.from,
      dateRange.to,
      searchDraft,
      transactionType,
    ],
  );

  const payloadData = {
    CurrencyID: currencyId,
    DateFrom: dateRange.from,
    DateTo: dateRange.to,
    TransactionType: transactionType,
    AccountType: accountSelected,
    Input: searchDraft,
  };

  const {
    items: statementItems,
    isInitialLoading: soaLoading,
    isLoadingMore,
    hasMore,
    error: statementsError,
    loadMoreError,
    loadMore,
    refresh: fetchStatements,
  } = useLoadMoreList({
    apiFunction: loadStatementRows,
    cursorKey: "lastDate",
    getCursor: (row) =>
      row?.RowDate ||
      row?.rowDate ||
      null,
    getItemId: (row) =>
      getStatementRowIdentity(row) ||
      row?.OrderID ||
      row?.id,
    resetKey: `${currencyId}|${accountSelected}|${dateRange.from}|${dateRange.to}|${transactionType}`,
  });

  const rows = useMemo(
    () =>
      (
        Array.isArray(statementItems)
          ? statementItems
          : []
      ).map((row) =>
        normalizeStatementRow(
          row,
          currencyId
        )
      ),
    [currencyId, statementItems]
  );

  const bslRowDetails = useMemo(
    () => isBslAccount ? getBslRowDetails(activeRow) : null,
    [activeRow, isBslAccount]
  );
  const pickerRange = useMemo(
    () => toPickerRange(dateRange),
    [dateRange]
  );

  const closeDetails = useCallback(() => {
    resetAnchor();
    setActiveRowIndex(-1);
    setActiveRow(null);
    resetTransactionDetails();
  }, [
    resetAnchor,
    resetTransactionDetails,
  ]);

  useEffect(() => {
    closeDetails();
    setExportMenuOpen(false);
    setListError("");

    GetSuyoolAccountBalance(
      dateRange.from,
      dateRange.to
    )
      .then((balances) => {
        onBalancesChange?.(balances);
      })
      .catch(() => { });
    // GetSuyoolAccountBalance is recreated by its API hook on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    closeDetails,
    currencyId,
    dateRange.from,
    dateRange.to,
    onBalancesChange,
    transactionType,
  ]);

  useEffect(() => {
    if (!statementsError) return;

    setListError(
      "Unable to load statement of account transactions right now."
    );
  }, [statementsError]);

  const handleOpenDetails = useCallback(
    async (row, rowIndex, event) => {
      if (!canViewDetails || hasZeroTransaction(row)) return;

      const baseId = getRowDetailsId(row, isBslAccount);

      if ((!baseId || baseId === "-") && !isBslAccount) return;

      anchorToRow(event);
      resetTransactionDetails();

      setActiveRowIndex(rowIndex);
      setActiveRow(row);

      const requestId = row?.isPayrollReference
        ? `${baseId}${currencyId === 1
          ? "-1"
          : "-2"
        }`
        : isBslAccount && baseId &&
          String(row?.status || "").toLowerCase() === "pending" &&
          !String(baseId).startsWith("ts")
          ? `ts${baseId}`
          : baseId || "";

      await GetTransactionDetails(
        requestId,
        "",
        row?.status ??
        row?.Status ??
        ""
      ).catch(() => { });
    },
    [
      GetTransactionDetails,
      anchorToRow,
      canViewDetails,
      currencyId,
      isBslAccount,
      resetTransactionDetails,
    ]
  );

  const handleExportExcel = useCallback(
    async () => {
      const exportRows =
        await GetSOAExport(
          currencyId,
          dateRange.from,
          dateRange.to,
          transactionType,
          accountSelected,
          searchDraft
        );

      exportToExcel({
        excelData: exportRows.map(
          (row) =>
            normalizeExportRow(
              row,
              currencyId
            )
        ),
        filename: `Transactions-${new Date(
          dateRange.to
        ).getTime()}`,
      });
    },
    [
      GetSOAExport,
      accountSelected,
      currencyId,
      dateRange.from,
      dateRange.to,
      searchDraft,
      transactionType,
    ]
  );

  const handleDownloadStatement = useCallback(
    async (row) => {
      const statementDetailID = row?.statementDetailID;
      if (!isBslAccount || !statementDetailID) return;

      setDownloadingStatementId(String(statementDetailID));

      try {
        if (
          !sessionStorage.getItem(
            "BSLAccessToken"
          )
        ) {
          await AccessToken();
        }

        const response =
          await DownloadTransferStatment(
            2,
            statementDetailID
          );

        if (
          String(
            response?.flagCode
          ) !== "0" ||
          !response?.data
        ) {
          throw new Error(
            "Statement link not returned"
          );
        }

        await downloadRemotePdf(
          response.data,
          `statement-${statementDetailID}.pdf`
        );
      } catch (error) {
        toast.error("Unable to download the statement right now.");
      } finally {
        setDownloadingStatementId(null);
      }
    },
    [
      AccessToken,
      DownloadTransferStatment,
      isBslAccount,
    ]
  );

  const columns = useMemo(
    () => [
      {
        header: "ID",
        accessor: "transaction",
        cell: ({ value }) => (
          <span className="transaction-content">
            {value}
          </span>
        ),
      },
      {
        header: "Date",
        accessor: "paymentDate",
      },
      {
        header: "Account",
        accessor: "account",
        cell: ({ value }) => (
          <span className="account-content">
            {value}
          </span>
        ),
      },
      {
        header: "Type",
        accessor: "type",
        cell: ({ value }) => (
          <span className="type-content">
            {value}
          </span>
        ),
      },
      {
        header: "Details",
        accessor: "description",
        cell: ({ value }) => (
          <span className="details-content">
            {value}
          </span>
        ),
      },
      {
        header: "Amount",
        accessor: "amount",
        cell: ({ row }) => {
          const colorClass =
            row.sign === "+"
              ? "is-positive"
              : row.sign === "-" &&
                String(
                  row.status || ""
                ).toLowerCase() ===
                "pending"
                ? "is-pending"
                : row.sign === "-"
                  ? "is-negative"
                  : "";

          return (
            <span
              className={`new-bank-transaction-history__money ${colorClass}`}
            >
              {row.sign === "+"
                ? "+"
                : row.sign === "-"
                  ? "-"
                  : ""}
              {formatAmount(
                Math.abs(
                  Number(
                    row.amount ?? 0
                  )
                ),
                selectedAccount
              )}
            </span>
          );
        },
      },
      {
        header: "Balance",
        accessor: "balance",
        cell: ({ row }) => (
          <span className="balance-content">
            {formatAmount(row.balance, selectedAccount)}
          </span>
        ),
      },
     {
  header: "Status",
  accessor: "status",

  cell: ({
    value,
    isUtcDateValue,
    renderTableCellValue,
  }) => {
    const status = String(value || "");

    if (
      status.toUpperCase() === "REVERSED"
    ) {
      return (
        <span className="soa-status soa-status--reversed">
          Reversed
        </span>
      );
    }

    return isUtcDateValue(status) ? (
      <span className="status-content">
        {renderTableCellValue(status)}
      </span>
    ) : (
      <span className="status-content">
        <StatusBadge
          value={capitalizeFirstLetters(
            status || "-"
          )}
        />
      </span>
    );
  },
},
      {
        id: "actions",
        header: "",
        align: "right",
        cell: ({ row, rowIndex }) => (
          <div
            className="new-s2s-page__actions"
            onClick={(event) =>
              event.stopPropagation()
            }
            onMouseDown={(event) =>
              event.stopPropagation()
            }
          >
            {row.isRepeated ? (
              <RepeatButton
                transactionId={
                  row.transaction
                }
                disabled={
                  row.status ===
                  "Declined"
                }
                onOpenQrAfterGenerate={
                  onOpenQrAfterGenerate
                }
              />
            ) : null}

            {canViewDetails && isBslAccount && row.statementDetailID ? (
              <button
                type="button"
                className="new-bank-transaction-history__row-action"
                onClick={(event) => {
                  event.stopPropagation();
                  void handleDownloadStatement(row);
                }}
                disabled={Boolean(downloadingStatementId)}
                aria-label={`Download statement ${row.statementDetailID}`}
                title={
                  downloadingStatementId === String(row.statementDetailID)
                    ? "Preparing statement..."
                    : "Download Statement"
                }
              >
                <FontAwesomeIcon icon={faFileArrowDown} />
              </button>
            ) : null}

            {!hasZeroTransaction(row) ? (
              <button
                type="button"
                className="new-bank-transaction-history__row-action"
                onClick={(event) => {
                  event.stopPropagation();
                  handleOpenDetails(row, rowIndex, event);
                }}
                disabled={!canViewDetails}
                aria-label={`View transaction ${row.transaction}`}
              >
                <FontAwesomeIcon icon={faChevronRight} />
              </button>
            ) : null}
          </div>
        ),
      },
    ],
    [
      canViewDetails,
      downloadingStatementId,
      handleDownloadStatement,
      handleOpenDetails,
      isBslAccount,
      onOpenQrAfterGenerate,
      selectedAccount,
    ]
  );


  return (
    <section
      className={`new-bank-transaction-history p-0 ${activeRowIndex >= 0
        ? "is-details-open"
        : ""
        }`}
    >
      <div className="new-bank-transaction-history__toolbar">
        <TableFilters
          className="new-bank-transaction-history__filters"
          dateRange={pickerRange}
          payload={payloadData}
          hasSearchButton={hasSearchButton}
          apiName={fetchStatements}
          onDateRangeChange={(range) => {
            const nextRange =
              fromPickerRange(
                range,
                createDefaultDateRange()
              );

            if (nextRange) {
              setDateRange(nextRange);
            }
          }}

          onSearchChange={setSearchDraft}
          searchValue={searchDraft}
          showDateRange
          selects={[
            {
              key: "transaction-type",
              className:
                "new-bank-transaction-history__select-filter",
              options:
                LEGAL_TRANSACTION_OPTIONS,
              value: transactionType,
              onChange:
                setTransactionType,
            },
          ]}
          customActions={
            <div className="new-bank-transaction-history__menu is-export-menu">
              <TableExportButton
                className="table__export__button"
                loading={exportLoading}
                onExport={() =>
                  setExportMenuOpen(
                    (current) =>
                      !current
                  )
                }
              />

              {exportMenuOpen ? (
                <div className="new-bank-transaction-history__menu-panel is-export-panel">
                  <TableExportButton
                    label="Export as PDF (.pdf)"
                    onExport={() => {
                      setExportMenuOpen(
                        false
                      );
                      setExportModalOpen(
                        true
                      );
                    }}
                    plain
                    variant="outline"
                  />

                  <TableExportButton
                    label="Export as Excel (.xlsx)"
                    loading={exportLoading}
                    onExport={() => {
                      setExportMenuOpen(
                        false
                      );
                      return handleExportExcel();
                    }}
                    plain
                    variant="outline"
                  />
                </div>
              ) : null}
            </div>
          }
        />
      </div>

      <div
        className="new-bank-transaction-history__body"
        ref={containerRef}
      >
        <div className="new-bank-transaction-history__table">
          <DataTable
            columns={columns}
            data={rows}
            loading={soaLoading}
            error={listError}
            errorMessage="Unable to load data right now."
            onRetry={() => {
              setListError("");

              fetchStatements().catch(
                () => {
                  setListError(
                    "Unable to load statement of account transactions right now."
                  );
                }
              );
            }}
            rowKey="id"
            rowClickable={(row) => canViewDetails && !hasZeroTransaction(row)}
            onRowClick={handleOpenDetails}
            getRowClassName={(
              row,
              rowIndex
            ) =>
              activeRowIndex ===
                rowIndex
                ? "is-active"
                : ""
            }
            emptyMessage="No transactions available."
            showLoadMore
            hasMore={hasMore}
            loadMoreLoading={
              isLoadingMore
            }
            loadMoreDisabled={
              soaLoading ||
              isLoadingMore
            }
            loadMoreError={
              loadMoreError
            }
            onLoadMore={loadMore}
          />
        </div>

        <div
          className="new-bank-transaction-history__details new-transaction-details-anchor"
          style={anchorStyle}
        >
          <TransactionDetailsPanel
            details={
              activeRowIndex >= 0
                ? transactionDetails || bslRowDetails
                : null
            }
            error={bslRowDetails ? null : detailsError}
            loading={
              activeRowIndex >= 0
                ? detailsLoading
                : false
            }
            onClose={closeDetails}
            hiddenKeys={[
              "statementDetailID",
              "StatementDetailID",
              "Transfer",
            ]}
          />
        </div>
      </div>

      <StatementExportModal
        isOpen={exportModalOpen}
        onClose={() =>
          setExportModalOpen(false)
        }
        currencyId={currencyId}
        dateFrom={dateRange.from}
        dateTo={dateRange.to}
        onConfirm={(fileType) => {
          setExportModalOpen(false);

          dispatch(
            setActionType(
              ACTION_TYPES.DOWNLOAD_SOA_PDF
            )
          );

          dispatch(
            setActionPayload({
              CurrencyId: currencyId,
              Email:
                sessionStorage.getItem(
                  "EmployeeEmail"
                ),
              DateFrom:
                dateRange.from,
              DateTo:
                dateRange.to,
              SOATypeId:
                fileType ===
                  "non-official"
                  ? 1
                  : 2,
            })
          );

          onOpenQrAfterGenerate?.();
        }}
      />
    </section>
  );
}

